import { useEffect, useState } from "react";
import { sentence, video, word } from "shared/types/Report";
import useFirestore from "shared/hooks/useFirestore";

type AllowedTypes = word | sentence | video;

interface file{
    url:string,
    index:number,
    type:string,
    audio?:boolean
}

const useView = <T extends AllowedTypes>(list: T[], setList: React.Dispatch<React.SetStateAction<T[]>>, path:string) => {
    const [selectedList, setSelectedList] = useState<number[]>([]);
    const [uploadFileName, setUploadFileName] = useState<string>("");
    const [progress, setProgress] = useState<number>(0);
    const [showProgress, setShowProgress] = useState<boolean>(false);
    const {writeFirestore, deleteFirestore} = useFirestore();
    const [isSaveSucceed, setIsSaveSucceed] = useState(false);
    const [isOpenBatchView, setIsOpenBatchView] = useState(false);
    const [selectedFile, setSelectedFile] = useState<file>({
        url:"",
        index:-1,
        type:""
    });

    const onChangeHandler = (index: number, name: string, value: File | string) => {
        const newWordList = list.map((item, idx) => {
            if (idx === index) {
                return { ...item, [name]: value };
            }
            return item;
        });
        setList(newWordList);
    };

    useEffect(() => {
        setSelectedFile({
            url:"",
            index:-1,
            type:""
        });
    }, [path])

    const onSelectHandler = (id: number, value: boolean) => {
        setSelectedList(prev => {
            if (value) {
                return [...prev, id];
            } else {
                return prev.filter(selectedId => selectedId !== id);
            }
        });
    };

    const onSelectAllClickHandler = (value: boolean) => {
        let id_list: number[] = [];
        if (value) {
            id_list = list.map((item) => item.id);
        }
        setSelectedList(id_list);
    };

    const onDeleteClickHandler = () => {
        setList(prev => prev.filter(item => !selectedList.includes(item.id)));
        setSelectedList([]);
    };

    const onRegisterReportClick = async(type:string, data:any) => {
        if(!data.length){
            await deleteFirestore(path + "/Content", type).then(res => {
                if(res){
                    setIsSaveSucceed(true);
                }
            })
            return;
        }
        await writeFirestore(path + "/Content", type, {content:data}).then(res => {
            if(res){
                setIsSaveSucceed(true);
            }
        })
    }

    const onSuccessModalConfirmHandler = () => {
        setIsSaveSucceed(false);
    }

    const removeKeyFromObjects = <T extends AllowedTypes, K extends keyof T>(
        array: T[], 
        keyToRemove: K
    ): Omit<T, K>[] => {
        return array.map(obj => {
            const { [keyToRemove]: _, ...rest } = obj;
            return rest as Omit<T, K>;
        });
    };

    const onFileClickHandler = (file:string, index:number, type:string, audio?:boolean) => {
        setSelectedFile({
            url:file,
            index:index,
            type:type,
            audio:audio
        });
    }

    const onFileModalCloseClickHandler = () => {
        setSelectedFile({
            url:"",
            index:-1,
            type:""
        });
    }

    const onBatchViewOpenClickHandler = () => {
        setIsOpenBatchView(true);
    }

    const onBatchViewCancelClickHandler = () => {
        setIsOpenBatchView(false);
    }

    return {
        onChangeHandler,
        selectedList,
        onSelectHandler,
        onDeleteClickHandler,
        onSelectAllClickHandler,
        onRegisterReportClick,
        isSaveSucceed,
        progress, setProgress,
        showProgress, setShowProgress,
        uploadFileName, setUploadFileName,
        removeKeyFromObjects,
        selectedFile, onFileClickHandler,
        onFileModalCloseClickHandler,
        onSuccessModalConfirmHandler,
        isOpenBatchView,
        onBatchViewCancelClickHandler,
        onBatchViewOpenClickHandler
    };
};

export default useView;